import { useRef, useState } from 'react'
import { router } from '@inertiajs/react'

// Keep the draft local: a response acknowledges only the revision it submitted,
// never edits made while that request was in flight.
export default function useImportReview<T extends object>(initial: T, path: string, ready: boolean) {
  const [mapping, setMapping] = useState(initial)
  const revision = useRef(0)
  const pending = useRef(false)
  const [dirty, setDirty] = useState(false)
  const [operation, setOperation] = useState<'save' | 'confirm' | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const update = (patch: Partial<T>) => {
    revision.current += 1
    setMapping(current => ({ ...current, ...patch }))
    setDirty(true)
  }
  const save = () => {
    if (pending.current) return
    pending.current = true
    const submittedRevision = revision.current
    let succeeded = false
    setOperation('save')
    setFailure(null)
    router.patch(path, { mapping: mapping as never }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        succeeded = true
        setDirty(revision.current !== submittedRevision)
      },
      onFinish: () => {
        pending.current = false
        setOperation(null)
        if (!succeeded) setFailure('The preview was not saved. Your edits are still here. Retry saving and refreshing the preview.')
      },
    })
  }
  const canConfirm = ready && !dirty && !operation && !failure
  const commit = (token: string | null, onFinish?: () => void) => {
    if (!canConfirm || !token || pending.current) return
    pending.current = true
    setOperation('confirm')
    let succeeded = false
    router.post(`${path}/confirm`, { confirmation_token: token }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => { succeeded = true },
      onFinish: () => {
        pending.current = false
        setOperation(null)
        if (!succeeded) setFailure('Import was not confirmed. Review the server error, then save and refresh the preview before trying again. Your edits are still here.')
        onFinish?.()
      },
    })
  }
  return { mapping, update, dirty, operation, failure, save, commit, canConfirm }
}
